import {
  lessValueFirst,
  worseEnforcementBalanceFirst,
} from "../../analytics/api/JudgmentCompareFns";
import { OutcomeSummary, OutcomesForParty } from "../../shared/Analytics";
import { ShownOutcome } from "../../shared/adjustedView";
import { Stage } from "../../shared/CaseConfiguration";
import { WeighedAverage } from "../../shared/Value";
import combineCompareFns from "../../utils/combineCompareFns";

const compareFn = combineCompareFns<OutcomeSummary>([
  worseEnforcementBalanceFirst,
  lessValueFirst,
]);

/** A single (x, y) point; structurally compatible with chart.js `Point`. */
export interface SeriesPoint {
  x: number;
  y: number;
}

/** Where on the scale of probability the outcomes settled in a stage lie. */
export type SettlementBand = { from: number; to: number; stage: Stage };

export interface RiskSeries {
  /** Judgments sorted worst-enforcement / least-value first (chart order). */
  readonly sortedJudgments: ShownOutcome[];
  /** The settled outcomes, one band of each stage where they follow on. */
  readonly settlementBands: SettlementBand[];
  /** "Enforcement result" line: per-judgment enforcement balance. */
  readonly judgmentEvs: SeriesPoint[];
  /**
   * "Net result" line: the authoritative per-judgment net result
   * (`OutcomeSummary.netResult`, i.e. `ledger.totals.netResult.party`). This is
   * the same value the Net Results tab plots — no independent recomputation.
   */
  readonly netResults: SeriesPoint[];
  /** Probability-weighted average enforcement balance ("value of the case"). */
  readonly averageEnforcement: number;
  readonly bestEnforcement?: number;
  readonly worstEnforcement?: number;
  readonly negativeOutcomes: number;
  readonly negativeOutcomesProbability: number;
  readonly positiveOutcomes: number;
  readonly positiveOutcomesProbability: number;
  readonly assetLimitReached: number;
  readonly assetLimitReachedProbability: number;
}

/**
 * Builds the risk-analysis chart series for a party's judgments.
 *
 * The net-result line is sourced directly from each judgment's ledger-backed
 * `netResult` scalar; it is NOT reconstructed from stage results, estimated
 * costs or the enforcement balance. Keeping this pure makes it unit-testable
 * (the repo has no React component test harness) and guarantees the Risk
 * Analysis and Net Results tabs share one source of truth.
 */
export function buildRiskSeries(
  outcome: OutcomesForParty<ShownOutcome>,
  assetLimit?: number | "unlimited",
): RiskSeries {
  const sortedJudgments = [...outcome.outcomes].sort(compareFn);

  const judgmentEvs: SeriesPoint[] = [];
  const settlementBands: SettlementBand[] = [];
  const netResults: SeriesPoint[] = [];
  const evAvg = new WeighedAverage();
  let probability = 0;
  let negativeOutcomes = 0;
  let negativeOutcomesProbability = 0;
  let positiveOutcomes = 0;
  let positiveOutcomesProbability = 0;
  let assetLimitReached = 0;
  let assetLimitReachedProbability = 0;
  let bestEnforcement: number | undefined;
  let worstEnforcement: number | undefined;

  sortedJudgments.forEach((judgment) => {
    const enforcement =
      judgment.enforcementVariants.length > 0
        ? judgment.enforcementVariants[0]
        : undefined;

    const enforcementBalance = enforcement ? enforcement.balance : 0;

    if (bestEnforcement)
      bestEnforcement = Math.max(enforcementBalance, bestEnforcement);
    else bestEnforcement = enforcementBalance;
    if (worstEnforcement)
      worstEnforcement = Math.min(enforcementBalance, worstEnforcement);
    else worstEnforcement = enforcementBalance;

    evAvg.add({ value: enforcementBalance, probability: judgment.probability });

    judgmentEvs.push({ x: probability, y: enforcementBalance });
    netResults.push({ x: probability, y: judgment.netResult });
    if (judgment.settledAt !== undefined) {
      const last = settlementBands[settlementBands.length - 1];
      if (last?.stage === judgment.settledAt && last.to === probability)
        last.to = probability + judgment.probability;
      else
        settlementBands.push({
          from: probability,
          to: probability + judgment.probability,
          stage: judgment.settledAt,
        });
    }

    probability += judgment.probability;
    if (enforcementBalance < 0) {
      negativeOutcomes++;
      negativeOutcomesProbability += judgment.probability;
    }
    // more than nothing, to the cent
    if (enforcementBalance >= 0.005) {
      positiveOutcomes++;
      positiveOutcomesProbability += judgment.probability;
    }
    if (
      assetLimit !== undefined &&
      assetLimit !== "unlimited" &&
      assetLimit <= -enforcementBalance
    ) {
      assetLimitReached++;
      assetLimitReachedProbability += judgment.probability;
    }
  });

  // Extend both stepped lines to 100% on the X axis.
  if (judgmentEvs.length > 0) {
    judgmentEvs.push({ x: 1, y: judgmentEvs[judgmentEvs.length - 1].y });
    netResults.push({ x: 1, y: netResults[netResults.length - 1].y });
  }

  return {
    sortedJudgments,
    settlementBands,
    judgmentEvs,
    netResults,
    averageEnforcement: evAvg.getOr(0),
    bestEnforcement,
    worstEnforcement,
    negativeOutcomes,
    negativeOutcomesProbability,
    positiveOutcomes,
    positiveOutcomesProbability,
    assetLimitReached,
    assetLimitReachedProbability,
  };
}
